import * as React from "react"

import { cn } from "../lib/utils"
import { Button } from "./ui/button"

type FilterBarProps = React.ComponentProps<"div"> & {
  onClear?: () => void
  clearLabel?: string
  rightSlot?: React.ReactNode
}

export function FilterBar({
  className,
  children,
  onClear,
  clearLabel,
  rightSlot,
  ...props
}: FilterBarProps) {
  return (
    <div
      data-slot="filter-bar"
      className={cn(
        "flex flex-wrap items-center gap-2 mb-4",
        className,
      )}
      {...props}
    >
      <div className="flex flex-wrap items-center gap-2 flex-1 min-w-0">
        {children}
        {onClear && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={onClear}
            className="text-muted-foreground hover:text-foreground"
          >
            {clearLabel ?? "Clear"}
          </Button>
        )}
      </div>
      {rightSlot && (
        <div className="flex items-center gap-2 shrink-0">
          {rightSlot}
        </div>
      )}
    </div>
  )
}

type FilterFieldProps = React.ComponentProps<"div"> & {
  label?: React.ReactNode
  grow?: boolean
  minWidth?: string | number
}

// Label is rendered sr-only (accessibility). Use the input's placeholder for
// visible context — that's how dashboard8 and most modern ERP UIs do it.
export function FilterField({
  className,
  label,
  grow,
  minWidth,
  children,
  style,
  ...props
}: FilterFieldProps) {
  const mergedStyle: React.CSSProperties = {
    ...style,
    ...(minWidth !== undefined
      ? { minWidth: typeof minWidth === "number" ? `${minWidth}px` : minWidth }
      : {}),
  }
  return (
    <div
      data-slot="filter-field"
      className={cn(
        grow && "flex-1 min-w-[180px] max-w-sm",
        className,
      )}
      style={mergedStyle}
      {...props}
    >
      {label && <span className="sr-only">{label}</span>}
      {children}
    </div>
  )
}
